/**
 * Purchase orders and goods receipts.
 *
 * `receive` takes an idempotency key from the caller, minted once when the
 * receiving form opens rather than inside the submit handler. A key generated at
 * submit time would be new on each retry — which is exactly the bug idempotency
 * exists to prevent, and here the consequence is stock on a shelf that does not
 * have it.
 */

import { api } from "@/api/client";

export type PurchaseOrderStatus =
  | "DRAFT"
  | "APPROVED"
  | "PARTLY_RECEIVED"
  | "RECEIVED"
  | "CANCELLED";

export interface PurchaseOrder {
  id: string;
  reference: string;
  supplier: string;
  supplier_name: string;
  status: PurchaseOrderStatus;
  expected_at: string | null;
  total_cost: string;
  line_count: number;
  note: string;
  created_by_name: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  cancelled_reason: string;
  created_at: string;
}

export interface PurchaseOrderLine {
  id: string;
  product: string;
  description: string;
  quantity_ordered: string;
  units_per_pack: string;
  pack_cost: string;
  line_cost: string;
}

export interface OutstandingLine {
  line_id: string;
  product_id: string;
  description: string;
  ordered_units: string;
  received_units: string;
  outstanding_units: string;
  unit_cost: string;
}

export interface PurchaseOrderDetail extends PurchaseOrder {
  lines: PurchaseOrderLine[];
  outstanding: OutstandingLine[];
}

export interface GoodsReceiptLine {
  id: string;
  product: string;
  product_name: string;
  quantity_received: string;
  unit_cost: string;
  line_cost: string;
  stock_movement_id: string | null;
}

export interface GoodsReceipt {
  id: string;
  reference: string;
  order: string | null;
  order_reference: string;
  supplier: string;
  supplier_name: string;
  section: string;
  supplier_reference: string;
  received_at: string;
  total_cost: string;
  note: string;
  received_by_name: string | null;
  lines: GoodsReceiptLine[];
  created_at: string;
}

interface Page<T> {
  count: number;
  next: string | null;
  results: T[];
}

function unwrap<T>(payload: Page<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}

export interface OrderLineInput {
  product_id: string;
  /** In packs, as ordered. Converted to units at receipt. */
  quantity: string;
  units_per_pack?: string;
  pack_cost?: string;
}

export interface ReceiptLineInput {
  product_id: string;
  /** In sellable units — the figure the stock ledger holds. */
  quantity: string;
  unit_cost?: string;
  order_line_id?: string;
}

export const procurementRepository = {
  orders: async (params: { status?: string; supplier?: string } = {}): Promise<PurchaseOrder[]> => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.supplier) query.set("supplier", params.supplier);
    return unwrap(
      await api.get<Page<PurchaseOrder> | PurchaseOrder[]>(`/purchase-orders/?${query.toString()}`),
    );
  },

  order: (id: string) => api.get<PurchaseOrderDetail>(`/purchase-orders/${id}/`),

  createOrder: (body: {
    supplier_id: string;
    branch_id?: string;
    lines: OrderLineInput[];
    expected_at?: string | null;
    note?: string;
  }) => api.post<PurchaseOrderDetail>("/purchase-orders/", body),

  replaceLines: (id: string, lines: OrderLineInput[]) =>
    api.patch<PurchaseOrderDetail>(`/purchase-orders/${id}/`, { lines }),

  approve: (id: string, reason = "") =>
    api.post<PurchaseOrder>(`/purchase-orders/${id}/approve/`, { reason }),

  cancel: (id: string, reason: string) =>
    api.post<PurchaseOrder>(`/purchase-orders/${id}/cancel/`, { reason }),

  receipts: async (orderId?: string): Promise<GoodsReceipt[]> =>
    unwrap(
      await api.get<Page<GoodsReceipt> | GoodsReceipt[]>(
        `/goods-receipts/${orderId ? `?order=${orderId}` : ""}`,
      ),
    ),

  receive: (
    body: {
      order_id?: string | null;
      supplier_id: string;
      section_id: string;
      lines: ReceiptLineInput[];
      supplier_reference?: string;
      received_at?: string;
      note?: string;
    },
    idempotencyKey: string,
  ) => api.post<GoodsReceipt>("/goods-receipts/", body, { idempotencyKey }),
};
